import { useState, useEffect, useMemo } from "react";
import { useSessionContext } from "../../contexts/SessionContext";
import { LoadingContent } from "../home/components";
import { EditableCardRow, LargeContentBox } from "../../components/common";
import EditEmailDialog from "./components/EditEmailDialog";
import { EditPhoneDialog } from "./components/EditPhoneDialog";
import { EditPasswordDialog } from "./components/EditPasswordDialog";
import { Stack, Box, useMediaQuery, useTheme } from "@mui/material";
import { CardInfo } from "./components/CardInfo";
import { CardContact } from "./components/CardContact";
import { CardPassword } from "./components/CardPassword";
import { CardDemographic } from "./components/CardDemographic";
import { TermOfUseDialog } from "../TermsOfUse/TermOfUseDialog";
import { useNavigate, useLocation } from "react-router-dom";
import { Logout, Person } from "@mui/icons-material";
import { InactivateAccount } from "./components/InactivateAccount/InactivateAccount";
import { PageHeader } from "@/components/common/PageHeader";
import { ScreenMobile } from "@/components/common/ScreenMobile";
import { ProfileSection } from "./components/ProfileSection";
import {
  getProfileFocusSection,
  scrollToProfileSectionWithRetry,
} from "./scrollToProfileSection";

/** Reserva extra no perfil mobile — somada ao padding do LayoutColapse. */
const PROFILE_MOBILE_EXTRA_CLEARANCE =
  "calc(16px + env(safe-area-inset-bottom, 0px))";

export function Profile() {
  const {
    access,
    setIsOpenTermDialog,
    isOpenTermDialog,
    setTerminateAccount,
    inviteDialog,
    loading,
  } = useSessionContext();

  const theme = useTheme();
  const isMobileLayout = useMediaQuery(theme.breakpoints.down("md"));

  const [isEditEmailOpen, setIsEditEmailOpen] = useState<boolean>(false);
  const [isEditPhoneOpen, setIsEditPhoneOpen] = useState<boolean>(false);
  const [isEditPasswordOpen, setIsEditPasswordOpen] = useState<boolean>(false);

  const [inactivateAccount, setInactivateAccount] = useState<boolean>(false);

  const navigate = useNavigate();
  const location = useLocation();
  const focusSection = useMemo(
    () => getProfileFocusSection(location.search, location.hash),
    [location.search, location.hash]
  );

  // Rola após o conteúdo montar (loading) — query `?focus=` é confiável no HashRouter.
  useEffect(() => {
    if (!focusSection || loading || inviteDialog) {
      return;
    }

    const cancelRetry = scrollToProfileSectionWithRetry(focusSection);

    return cancelRetry;
  }, [focusSection, loading, inviteDialog]);

  if (loading || inviteDialog) {
    return <LoadingContent />;
  }

  const profileContent = (
    <>
      {isEditEmailOpen && (
        <EditEmailDialog
          isOpen={isEditEmailOpen}
          setIsOpen={setIsEditEmailOpen}
        />
      )}

      {isEditPhoneOpen && (
        <EditPhoneDialog
          isOpen={isEditPhoneOpen}
          setIsOpen={setIsEditPhoneOpen}
        />
      )}

      {isEditPasswordOpen && (
        <EditPasswordDialog
          isOpen={isEditPasswordOpen}
          setIsOpen={setIsEditPasswordOpen}
        />
      )}

      <Stack
        spacing={{ xs: 1.5, sm: 2, md: 3 }}
        width="100%"
        alignItems="stretch"
      >
        {!isMobileLayout && (
          <PageHeader
            title="Informações pessoais"
            icon={Person}
            centered
            description="Informações pessoais e opções de gerenciamento do seu perfil nos serviços Fluxmed"
          />
        )}

        <div id="info-basicas" style={{ width: "100%" }}>
          <CardInfo />
        </div>

        <CardDemographic />

        <CardContact
          setIsEditEmailOpen={setIsEditEmailOpen}
          setIsEditPhoneOpen={setIsEditPhoneOpen}
        />

        <CardPassword onClick={() => setIsEditPasswordOpen(true)} />

        <ProfileSection title="Informações adicionais">
          <EditableCardRow
            title="Cancelamento de conta"
            titleContext=""
            auxText="Cancelar minha conta"
            auxIcon={<Logout />}
            colorButton="error700.main"
            onClick={() => setInactivateAccount(true)}
          />
          <EditableCardRow
            title="Termos de uso"
            titleContext=""
            auxText="Visualizar termos de uso"
            onClick={() => setIsOpenTermDialog(true)}
          />
        </ProfileSection>

        {isMobileLayout && access?.type === "patient" && (
          <Box
            aria-hidden
            sx={{
              width: "100%",
              height: PROFILE_MOBILE_EXTRA_CLEARANCE,
              flexShrink: 0,
            }}
          />
        )}
      </Stack>

      {isOpenTermDialog && <TermOfUseDialog isOpen={isOpenTermDialog} />}
      {inactivateAccount && (
        <InactivateAccount
          isOpen={inactivateAccount}
          handleClose={() => setInactivateAccount(false)}
          onClick={() => {
            setTerminateAccount(true);
            navigate("/terms");
          }}
        />
      )}
    </>
  );

  if (isMobileLayout) {
    return (
      <ScreenMobile
        open
        onClose={() => navigate("/home")}
        title="Informações pessoais"
        subtitle="Gerencie seu perfil nos serviços Fluxmed"
      >
        <Box sx={{ width: "100%", pb: 1 }}>{profileContent}</Box>
      </ScreenMobile>
    );
  }

  return (
    <LargeContentBox>
      <Box sx={{ width: "100%", justifySelf: "stretch" }}>{profileContent}</Box>
    </LargeContentBox>
  );
}
